import { isEmpty } from "lodash";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import TextInput from "../Common/Forms/TextInput";
import ModalGeneric from "../Common/ModalGeneric";
import Spinner from "../Common/Spinner";
import KlageSearchForm from "./KlageSearchForm";

import {
  getArkivsakSak,
  nullStilleKlageSidenState,
} from "../../actions/arkivsakAction";
import {
  handleChange as handleChangeAction,
  handleChangeToInitState as handleChangeToInitStateAction,
} from "../../actions/handleChangeAction";
import {
  addKlage,
  deletePermanentKlage,
  getKlageInfo,
  updateKlage,
} from "../../actions/klager/klageAction";
import {
  KLAGE_ILEGGELSER_FILTER_FORM,
  INITIAL_STATE as klageIleggelseFilterFormStateInitial,
} from "../../reducers/forms/klageIleggelseFilterFormReducer";
import klageUtils from "../../utils/klageUtils";
import { replaceWhitespacesWithEmptyStrings } from "../../utils/sanitizationUtils";

import { ikkeGyldigSaksnummer } from "../../constants/ErrorMessages";

function KlageSearchFormContainer() {
  const dispatch = useDispatch();
  const KlageIleggelserFilterFormState = useSelector(
    (state) => state.forms.KlageIleggelseFilterFormState,
  );
  const vedtaker = useSelector((state) => state.vedtaker);

  const [sak, setSak] = useState({});
  const [shallGetIleggelserDueToHandleChangeToInitState, setShallGet] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSokSpinner, setShowSokSpinner] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showKobleTilP360, setShowKobleTilP360] = useState(false);
  const [kobleTilP360SubmitSpinner, setKobleTilP360SubmitSpinner] =
    useState(false);
  const [arkivSaksnummer, setArkivSaksnummer] = useState(undefined);
  const [
    arkivsakSaksnummerValidationError,
    setArkivsakSaksnummerValidationError,
  ] = useState(undefined);
  const [inputSaksnummer, setInputSaksnummer] = useState(null);
  const [
    showKlageSearchWithoutFerdigbehandletVedtakConfirmationModal,
    setShowKlageSearchWithoutFerdigbehandletVedtakConfirmationModal,
  ] = useState(false);

  useEffect(() => {
    if (
      JSON.stringify(KlageIleggelserFilterFormState) ===
        JSON.stringify(klageIleggelseFilterFormStateInitial) &&
      shallGetIleggelserDueToHandleChangeToInitState === true
    ) {
      setShallGet(false);
    }
  }, [
    KlageIleggelserFilterFormState,
    shallGetIleggelserDueToHandleChangeToInitState,
  ]);

  const klagePayload = (saksnummer, kontakt) => ({
    saksnummer,
    navn: (kontakt && kontakt.navn) || "",
    gateadresse: (kontakt && kontakt.adresse) || "",
    poststed: (kontakt && kontakt.poststed) || "",
    postnummer: (kontakt && kontakt.postnummer) || "",
    land: (kontakt && kontakt.land) || "",
    epost: (kontakt && kontakt.epost) || "",
  });

  const handleChange = (e) => {
    dispatch(
      handleChangeAction(
        KLAGE_ILEGGELSER_FILTER_FORM,
        e.target.name,
        e.target.value,
      ),
    );
  };

  const handleChangeToInitState = () => {
    dispatch(handleChangeToInitStateAction(KLAGE_ILEGGELSER_FILTER_FORM));
    setShallGet(true);
    dispatch(nullStilleKlageSidenState());
  };

  const handleBlur = (e) => {
    if (e.target.name === "saksnummer") {
      dispatch(
        handleChangeAction(
          KLAGE_ILEGGELSER_FILTER_FORM,
          e.target.name,
          replaceWhitespacesWithEmptyStrings(e.target.value),
        ),
      );
    }
  };

  const validateSaksnummer = (saksnummerVal) => {
    const inputSak =
      saksnummerVal && replaceWhitespacesWithEmptyStrings(saksnummerVal);
    if (!inputSak) return false;
    if (!klageUtils.getSaksnummerObject(inputSak)) return false;
    return inputSak;
  };

  const getHasIkkeFerdigbehandletVedtaker = () => {
    const vedtakerArray = Object.values(vedtaker);
    for (const vedtak of vedtakerArray) {
      if (vedtak.vedtaksDato === null) return true;
    }
    return false;
  };

  const confirmSakSearch = async (e, arkivsakParam = undefined) => {
    setShowConfirmation(false);
    const arkivsakVar =
      arkivsakParam && !isEmpty(arkivsakParam) ? arkivsakParam : sak;
    const inputSak = validateSaksnummer(
      KlageIleggelserFilterFormState.saksnummer,
    );
    let klage;
    if (!isEmpty(arkivsakVar)) {
      klage = await dispatch(getKlageInfo(inputSak));
    }
    if (!isEmpty(klage)) {
      await dispatch(
        updateKlage({
          ...klagePayload(inputSak, arkivsakVar.kontakt),
          id: klage.id,
        }),
      );
      klage = await dispatch(getKlageInfo(inputSak));
    }
    if (isEmpty(klage) && !isEmpty(arkivsakVar)) {
      const kontakt = arkivsakVar.kontakt;
      await dispatch(addKlage(klagePayload(inputSak, kontakt)));
    }
    setIsLoading(false);
    setShowSokSpinner(false);
  };

  const onKlageSearchSubmitJob = async (validSaksnummer) => {
    const saksnummerObject = klageUtils.getSaksnummerObject(validSaksnummer);
    try {
      setIsLoading(true);
      setShowSokSpinner(true);
      const sakResult = await dispatch(getArkivsakSak(saksnummerObject));
      setIsLoading(false);
      setShowSokSpinner(false);
      if (sakResult) {
        setSak(sakResult);
        if (KlageIleggelserFilterFormState.kobleTilP360sak) {
          setShowKobleTilP360(true);
          setIsLoading(false);
          setShowSokSpinner(false);
          const inputArkivSaksnummerElement =
            document.getElementById("arkivSaksnummer");
          inputArkivSaksnummerElement.focus();
          return;
        } else if (
          !KlageIleggelserFilterFormState.kobleTilP360 &&
          sakResult.accessGroup &&
          sakResult.accessGroup.toUpperCase() !== "KLAGE"
        ) {
          setShowConfirmation(true);
        } else {
          await confirmSakSearch(null, sakResult);
        }
      }
    } catch (err) {
      setIsLoading(false);
      setShowSokSpinner(false);
    }
  };

  const onKlageSearchSubmit = async (e) => {
    e.preventDefault();
    const validSaksnummer = validateSaksnummer(
      KlageIleggelserFilterFormState.saksnummer,
    );
    if (validSaksnummer && !getHasIkkeFerdigbehandletVedtaker()) {
      onKlageSearchSubmitJob(validSaksnummer);
    } else if (validSaksnummer && getHasIkkeFerdigbehandletVedtaker()) {
      setInputSaksnummer(validSaksnummer);
      setShowKlageSearchWithoutFerdigbehandletVedtakConfirmationModal(true);
    }
  };

  const onConfirmKlageSearchSubmitJob = () => {
    onKlageSearchSubmitJob(inputSaksnummer);
    setShowKlageSearchWithoutFerdigbehandletVedtakConfirmationModal(false);
  };

  const closeKlageSearchWithoutFerdigbehandletVedtakConfirmationModal = () => {
    setShowKlageSearchWithoutFerdigbehandletVedtakConfirmationModal(false);
  };

  const closeModal = () => {
    setShowConfirmation(false);
    setShowKobleTilP360(false);
    setSak({});
  };

  const openModal = () => setShowConfirmation(true);

  const submitKobleTilP360sak = async (e) => {
    e.preventDefault();

    if (!validateSaksnummer(arkivSaksnummer)) {
      setArkivsakSaksnummerValidationError(ikkeGyldigSaksnummer);
      return;
    }
    setArkivsakSaksnummerValidationError(null);
    setKobleTilP360SubmitSpinner(true);

    const arkivKlageFraHgk = await dispatch(
      getKlageInfo(arkivSaksnummer, false, null),
    );
    if (!!!isEmpty(arkivKlageFraHgk)) {
      const klageSak = await dispatch(
        getKlageInfo(KlageIleggelserFilterFormState.saksnummer),
      );
      let shouldUpdateKlage = true;
      if (!!!isEmpty(klageSak)) {
        await dispatch(deletePermanentKlage(klageSak.id)).catch(() => {
          setKobleTilP360SubmitSpinner(false);
          shouldUpdateKlage = false;
        });
      }

      if (shouldUpdateKlage) {
        await dispatch(
          updateKlage({
            ...klagePayload(
              KlageIleggelserFilterFormState.saksnummer,
              sak.kontakt,
            ),
            id: arkivKlageFraHgk.id,
          }),
        );
        await dispatch(getKlageInfo(KlageIleggelserFilterFormState.saksnummer));
      }

      setShowKobleTilP360(false);
      setKobleTilP360SubmitSpinner(false);
      return;
    }

    setKobleTilP360SubmitSpinner(false);
  };

  const handleArkivSaksnummerChange = (e) => {
    setArkivSaksnummer(e.target.value);
  };

  return (
    <div className="kalge">
      <KlageSearchForm
        showSokSpinner={showSokSpinner}
        KlageIleggelseFilterFormState={KlageIleggelserFilterFormState}
        handleChange={handleChange}
        handleBlur={handleBlur}
        handleChangeToInitState={handleChangeToInitState}
        onSubmit={onKlageSearchSubmit}
      />
      {showKobleTilP360 && (
        <ModalGeneric
          id="kobleTilP360"
          modalTitle={"Finn gammel P360-sak og endre til nytt Acos-nummer"}
          open={showKobleTilP360}
          closeModal={closeModal}
          submit={submitKobleTilP360sak}
          showSubmitSpinner={kobleTilP360SubmitSpinner}
          submitButtonText="Endre"
          isForm
        >
          <TextInput
            id="p360Saksnummer"
            name="p360Saksnummer"
            label="Saksnummer for P360-sak"
            placeholder="Saksnummer"
            onChange={handleArkivSaksnummerChange}
            validationError={arkivsakSaksnummerValidationError}
          />
        </ModalGeneric>
      )}
      {showConfirmation && (
        <ModalGeneric
          id="ids"
          modalTitle={"Saksnummer uten klage"}
          open={showConfirmation}
          closeModal={closeModal}
          openModal={openModal}
          submit={confirmSakSearch}
          submitButtonText="Fortsett"
        >
          <p>Saksnummer har ingen klage, vil du fortsette? </p>
        </ModalGeneric>
      )}
      {showKlageSearchWithoutFerdigbehandletVedtakConfirmationModal && (
        <ModalGeneric
          id="sifvc"
          modalTitle="Minst ett vedtak er ikke ferdigbehandlet."
          open={showKlageSearchWithoutFerdigbehandletVedtakConfirmationModal}
          closeModal={
            closeKlageSearchWithoutFerdigbehandletVedtakConfirmationModal
          }
          submit={onConfirmKlageSearchSubmitJob}
          submitButtonText="Fortsett"
        >
          <p>Vil du gå videre uten å ferdigbehandle vedtakene?</p>
        </ModalGeneric>
      )}
      <Spinner isLoading={isLoading} />
    </div>
  );
}

KlageSearchFormContainer.propTypes = {
  KlageIleggelserFilterFormState: PropTypes.object,
  vedtaker: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.arrayOf(PropTypes.shape({})),
  ]),
};

export default KlageSearchFormContainer;
